import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';

import {
  FieldKinds,
  StyleVariants,
  TableCellData,
  TableCellKinds,
  Template,
} from '@site-mate/dashpivot-shared-library';

import { TemplateTableFormulaCellComponent } from 'app/form-fields/table/template/table-cell-items/formula/template-table-formula-cell.component';
import { TemplateTableListCellComponent } from 'app/form-fields/table/template/table-cell-items/list/template-table-list-cell.component';
import { FieldWeb } from 'app/shared/model/item.model';

import { TemplateTableListPropertyCellComponent } from './table-cell-items/list-property/template-table-list-property-cell.component';
import { ITemplateTableCell } from './table-cell-items/template-table-cell.interface';

@Component({
  selector: 'cc-app-table-column',
  templateUrl: 'app-table-column.component.html',
  styleUrls: [
    '../../common/template/form-control.component.scss',
    'app-table.component.scss',
    'app-table-column.component.scss',
  ],
  // TODO: this prevents changes in formulas being seen
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnTemplateComponent {
  readonly TableCellKinds = TableCellKinds;
  readonly disabledColumnKinds = new Set([TableCellKinds.ListProperty]);

  private readonly cdr = inject(ChangeDetectorRef);
  editingPrefillText: boolean;

  @Input() column: TableCellData;
  @Input() listItems: Observable<any[]>;
  @Input() index: number;
  @Input() item: FieldWeb;
  @Input() companyId: string;
  @Input() template: Template;
  @Output() updated: EventEmitter<any> = new EventEmitter();
  @Output() formulaChange: EventEmitter<string> = new EventEmitter();

  id = Date.now();
  editorToolbarOption = [
    ['bold', 'underline'],
    [{ script: 'sub' }, { script: 'super' }],
    [{ color: [] }],
    [{ list: 'bullet' }],
    ['image'],
  ];
  columnTypes: { type: TableCellKinds; label: string }[] = [
    { type: TableCellKinds.Attachment, label: 'Attachment' },
    { type: TableCellKinds.Date, label: 'Date' },
    { type: TableCellKinds.Formula, label: 'Formula' },
    { type: TableCellKinds.List, label: 'List' },
    { type: TableCellKinds.ManualSignature, label: 'Sign manually' },
    { type: TableCellKinds.Number, label: 'Number' },
    { type: TableCellKinds.Photo, label: 'Photo/Video reference' },
    { type: TableCellKinds.PrefilledText, label: 'Prefilled text' },
    { type: TableCellKinds.Signature, label: 'Signature' },
    { type: TableCellKinds.Text, label: 'Text' },
    { type: TableCellKinds.Time, label: 'Time' },
  ];

  @ViewChild('tableCell', { read: ViewContainerRef }) dynamicCell: ViewContainerRef;

  ngAfterViewInit() {
    this.renderCellComponent();
  }

  onColumnKindChange(newColumn: TableCellData) {
    this.emptyColumnData(newColumn);
    this.updated.emit(newColumn);
    this.cdr.detectChanges();
    this.renderCellComponent();
  }

  private emptyColumnData(column: TableCellData) {
    column.reference = '';
    column.content = '';
    column.description = '';
    column.manualListItems = undefined;
    column.listSource = undefined;
    delete column.formula;
    delete column.formulaError;

    if (column.kind === TableCellKinds.Formula) {
      column.formula = '';
      column.formulaError = '';
    }

    if (column.kind === TableCellKinds.List) {
      column.defaultColor = StyleVariants.Sky;
    }
  }

  private get CellComponent(): Type<ITemplateTableCell> {
    const cellComponentCreators = {
      [TableCellKinds.List]: TemplateTableListCellComponent,
      [TableCellKinds.Formula]: TemplateTableFormulaCellComponent,
      [TableCellKinds.ListProperty]: TemplateTableListPropertyCellComponent,
    };

    return cellComponentCreators[this.column.kind];
  }

  private renderCellComponent(): void {
    if (!this.dynamicCell) {
      return;
    }
    const component = this.dynamicCell.createComponent(this.CellComponent);
    component.instance.cellData = this.column;

    if (component.instance instanceof TemplateTableFormulaCellComponent) {
      component.instance.tableId = this.item._id;
      component.instance.change = this.formulaChange;
    }

    if (component.instance instanceof TemplateTableListCellComponent) {
      component.instance.table = this.item;
      component.instance.index = this.index;
      component.instance.isDefaultTable = this.item.kind === FieldKinds.Table;
      component.instance.isOrganisationTemplate = this.template.isOrganisationTemplate;
    }

    if (component.instance instanceof TemplateTableListPropertyCellComponent) {
      component.instance.tableColumns = this.item.columns;
    }

    this.cdr.detectChanges();
  }

  setEditingPrefillText(value: boolean) {
    this.editingPrefillText = value;
  }
}
